import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid";
import clsx from "clsx";
import type { SimpleAssetDefinition } from "kubb";
import type { ParseResult } from "papaparse";
import type React from "react";
import { useState } from "react";
import * as yup from "yup";
import AlertBanner from "../../../components/Banner/AlertBanner";
import BulkUpdateCSVDropZone from "../../../components/BulkUpdate/BulkUpdateCSVDropZone";
import { ErrorBanner } from "../../../components/Error/ErrorBanner";
import BlockSpinner from "../../../components/Spinners/BlockSpinner";
import { Button } from "../../../components/Theme/button";
import { Heading } from "../../../components/Theme/heading";
import { removeMapBlanks } from "../../../utils/object.tsx";
import {
  type UploadDefinition,
  convertCSVToDefinitions,
} from "../assetPositions/simpleAssetRegisterUpload/GenericUploadPage";

const VALID_ASSET_TYPES = [
  "heat_meter",
  "cooling_meter",
  "electricity_meter",
  "gas_meter",
  "water_meter",
  "heating_and_cooling_meter",
];

const ASSET_TYPE_LABELS: Record<string, string> = {
  heat_meter: "Heat",
  cooling_meter: "Cooling",
  electricity_meter: "Electricity",
  gas_meter: "Gas",
  water_meter: "Water",
  heating_and_cooling_meter: "Heat & Cool",
};

const SUPPORTED_UNITS = [
  "kWh",
  "MWh",
  "GJ",
  "Wh",
  "m3",
  "l",
  "dm3",
  "m3/h",
  "m3-h",
  "l/h",
  "l/min",
  "kW",
  "MW",
  "W",
  "C",
  "°C",
  "degC",
  "deg C",
];

const STANDARDISED_COLUMNS = [
  "Energy (Heating)",
  "Energy (Cooling)",
  "Energy (Electrical)",
  "Power",
  "Flow",
  "Flow Temperature",
  "Return Temperature",
  "Temperature",
  "Volume",
];

const UNIT_ALIASES: Record<string, string> = {
  degc: "°C",
  "deg c": "°C",
  "deg. c": "°C",
  celsius: "°C",
  "degrees c": "°C",
  "m3-h": "m3/h",
  "m³": "m3",
  "m³/h": "m3/h",
};

function normaliseUnit(unit: string): string {
  const trimmed = unit.trim();
  return UNIT_ALIASES[trimmed.toLowerCase()] ?? trimmed;
}

/** Which normalised units are valid for each standardised column category. */
const ENERGY_UNITS = new Set(["Wh", "kWh", "MWh", "GJ"]);
const POWER_UNITS = new Set(["W", "kW", "MW"]);
const VOLUME_UNITS = new Set(["m3", "l", "dm3"]);
const FLOW_UNITS = new Set(["m3/h", "l/h", "l/min"]);
const TEMPERATURE_UNITS = new Set(["C", "°C"]);

const ALL_VALID_UNITS = new Set([
  ...ENERGY_UNITS,
  ...POWER_UNITS,
  ...VOLUME_UNITS,
  ...FLOW_UNITS,
  ...TEMPERATURE_UNITS,
]);

const COLUMN_TO_VALID_UNITS: Record<string, Set<string>> = {
  "Energy (Heating)": ENERGY_UNITS,
  "Energy (Cooling)": ENERGY_UNITS,
  "Energy (Electrical)": ENERGY_UNITS,
  Power: POWER_UNITS,
  Volume: VOLUME_UNITS,
  Flow: FLOW_UNITS,
  "Flow Temperature": TEMPERATURE_UNITS,
  "Return Temperature": TEMPERATURE_UNITS,
  Temperature: TEMPERATURE_UNITS,
};

function isValidUnit(raw: string): boolean {
  return ALL_VALID_UNITS.has(normaliseUnit(raw));
}

function isUnitValidForColumn(raw: string, column: string): boolean {
  const normalised = normaliseUnit(raw);
  const allowed = COLUMN_TO_VALID_UNITS[column];
  if (!allowed) return ALL_VALID_UNITS.has(normalised);
  return allowed.has(normalised);
}

const fieldMappingSchema = yup.object().shape({
  identifier_value: yup.string().required("identifier_value is required"),
  standardised_column: yup.string().required("standardised_column is required"),
  units: yup
    .string()
    .required("units is required")
    .test(
      "valid-unit",
      (val) =>
        `Unknown unit "${val?.value}". Valid: ${[...ALL_VALID_UNITS].join(", ")}`,
      (val) => !val || isValidUnit(val),
    ),
});

const fieldMappingWithColumnSchema = yup
  .object()
  .shape({
    identifier_value: yup.string().required("identifier_value is required"),
    standardised_column: yup
      .string()
      .required("standardised_column is required"),
    units: yup.string().required("units is required"),
  })
  .test(
    "unit-matches-column",
    "Unit is not valid for this field type",
    (val) => {
      if (!val?.units || !val?.standardised_column) return true;
      if (!isValidUnit(val.units)) return true; // already caught by unit validation
      return isUnitValidForColumn(val.units, val.standardised_column);
    },
  );

const importSchema = yup.object().shape({
  serial_number: yup.string().required("serial_number is required"),
  asset_type: yup
    .string()
    .required("asset_type is required")
    .oneOf(
      VALID_ASSET_TYPES,
      `Must be one of: ${VALID_ASSET_TYPES.join(", ")}`,
    ),
  address: yup.string().required("address is required"),
  asset_position_reference: yup.string(),
  location_reference: yup.string(),
  group: yup
    .string()
    .matches(
      /^\/?[\w\u00C0-\u024F\-_.,'():&\[\]+]+(\/[\w\u00C0-\u024F\-_.,'():&\[\]+]+)*\/?$/u,
      { message: "Group contains invalid characters" },
    )
    .test(
      "max-tiers",
      "Maximum of 8 group tiers allowed",
      (v) => !v || v.replace(/^\/|\/$/g, "").split("/").length <= 8,
    ),
  fields: yup
    .array()
    .of(fieldMappingWithColumnSchema)
    .min(1, "At least one field mapping is required"),
});

type ParsedDefinition = UploadDefinition<SimpleAssetDefinition>;

interface FieldMapping {
  identifier_value: string;
  standardised_column: string;
  units: string;
}

function transformToSimpleDefinition(
  validated: Record<string, unknown>,
): SimpleAssetDefinition {
  const serialNumber = validated.serial_number as string;
  const assetType = validated.asset_type as string;
  const fields = (validated.fields as FieldMapping[]).map((f) => ({
    ...f,
    units: normaliseUnit(f.units),
  }));

  return {
    serial_number: serialNumber,
    asset_type: assetType as SimpleAssetDefinition["asset_type"],
    address: validated.address as string,
    asset_position_reference:
      (validated.asset_position_reference as string) || undefined,
    location_reference: (validated.location_reference as string) || undefined,
    group: (validated.group as string) || undefined,
    amr_metadata: {
      type: "generic_csv_v1" as const,
      identifier: serialNumber,
      asset_type: assetType,
    },
    configuration: {
      generic_csv: {
        fields: fields,
      },
    },
  };
}

/** Collect all unique field columns across all definitions for dynamic table headers. */
function collectFieldColumns(
  definitions: ParsedDefinition[],
): { standardised_column: string; units: string }[] {
  const result: { standardised_column: string; units: string }[] = [];
  const seen = new Set<string>();
  for (const def of definitions) {
    const config = (def.object?.configuration as Record<string, unknown>)
      ?.generic_csv as { fields: FieldMapping[] } | undefined;
    if (!config?.fields) continue;
    for (const f of config.fields) {
      const key = JSON.stringify([f.standardised_column, f.units]);
      if (!seen.has(key)) {
        seen.add(key);
        result.push({
          standardised_column: f.standardised_column,
          units: f.units,
        });
      }
    }
  }
  return result;
}

function PreviewTable({ definitions }: { definitions: ParsedDefinition[] }) {
  const validCount = definitions.filter((d) => d.success).length;
  const errorCount = definitions.filter((d) => !d.success).length;
  const fieldColumns = collectFieldColumns(definitions);

  return (
    <div className="space-y-3">
      <div className="text-sm">
        {validCount > 0 ? (
          <p>
            Ready to create <strong>{validCount}</strong> asset
            {validCount !== 1 ? "s" : ""}.
            {errorCount > 0 && (
              <span className="text-amber-600 dark:text-amber-400 ml-2">
                {errorCount} row{errorCount !== 1 ? "s" : ""} with errors (will
                be skipped).
              </span>
            )}
          </p>
        ) : (
          <p className="text-red-600">
            No valid rows found. Please check your CSV.
          </p>
        )}
      </div>

      <div className="overflow-x-auto">
        <table
          className="text-sm divide-y divide-zinc-200 dark:divide-zinc-700 border border-zinc-200 dark:border-zinc-700"
          style={{ fontFamily: "monospace" }}
        >
          <thead>
            <tr className="text-left text-xs font-medium text-zinc-500 dark:text-zinc-400 whitespace-nowrap divide-x divide-zinc-200 dark:divide-zinc-700">
              <th className="px-2 py-2 w-8" />
              <th className="px-2 py-2">Serial</th>
              <th className="px-2 py-2">Type</th>
              <th className="px-2 py-2">Address</th>
              {fieldColumns.map((fc) => (
                <th
                  key={`${fc.standardised_column}-${fc.units}`}
                  className="px-2 py-2 whitespace-nowrap"
                >
                  <div>{fc.standardised_column}</div>
                  <div className="font-normal text-zinc-400">({fc.units})</div>
                </th>
              ))}
              <th className="px-2 py-2">Errors</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {definitions.map((def, i) => {
              const hasErrors = def.errors && def.errors.length > 0;
              const original = def.original as Record<string, unknown>;
              const config = (
                def.object?.configuration as Record<string, unknown>
              )?.generic_csv as { fields: FieldMapping[] } | undefined;
              const fieldMap = new Map(
                (config?.fields ?? []).map((f) => [
                  JSON.stringify([f.standardised_column, f.units]),
                  f.identifier_value,
                ]),
              );

              return (
                <tr
                  key={`row-${(original?.serial_number as string) || ""}-${i}`}
                  className={clsx(
                    "divide-x divide-zinc-100 dark:divide-zinc-800",
                    hasErrors && "bg-amber-50 dark:bg-amber-900/10",
                  )}
                >
                  <td className="px-2 py-2">
                    {hasErrors ? (
                      <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
                    ) : (
                      <CheckCircleIcon className="h-4 w-4 text-green-500" />
                    )}
                  </td>
                  <td className="px-2 py-2 text-xs whitespace-nowrap">
                    {(original?.serial_number as string) || "—"}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    {ASSET_TYPE_LABELS[original?.asset_type as string] ||
                      (original?.asset_type as string) ||
                      "—"}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    {(original?.address as string) || "—"}
                  </td>
                  {fieldColumns.map((fc) => {
                    const key = JSON.stringify([
                      fc.standardised_column,
                      fc.units,
                    ]);
                    const idValue = fieldMap.get(key);
                    return (
                      <td
                        key={key}
                        className="px-2 py-2 text-xs whitespace-nowrap"
                      >
                        {idValue || (
                          <span className="text-zinc-300 dark:text-zinc-600">
                            —
                          </span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-2 py-2 text-xs text-red-600 dark:text-red-400 max-w-64">
                    {hasErrors
                      ? def.errors
                          ?.map((e) =>
                            e.field ? `${e.field}: ${e.error}` : e.error,
                          )
                          .join("; ")
                      : ""}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface GenericCSVAssetUploadProps {
  onUpload: (definitions: SimpleAssetDefinition[]) => Promise<unknown>;
  loading: boolean;
  error?: Error;
}

function ReferenceSection({
  title,
  items,
}: { title: string; items: string[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
      >
        {open ? "Hide" : "Show"} {title}
      </button>
      {open && (
        <div className="mt-1 flex flex-wrap gap-1.5">
          {items.map((item) => (
            <code
              key={item}
              className="text-xs bg-zinc-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded"
            >
              {item}
            </code>
          ))}
        </div>
      )}
    </div>
  );
}

export default function GenericCSVAssetUpload(
  props: GenericCSVAssetUploadProps,
): React.ReactElement {
  const { onUpload, loading, error } = props;
  const [definitions, setDefinitions] = useState<ParsedDefinition[]>([]);
  const [state, setState] = useState<"upload" | "preview" | "complete">(
    "upload",
  );
  const [parsing, setParsing] = useState(false);

  const handleFile = (
    data: Promise<ParseResult<Record<string, unknown>>>,
  ): void => {
    setParsing(true);
    convertCSVToDefinitions<SimpleAssetDefinition>(data, importSchema)
      .then((results) => {
        const transformed = results.map((r) => {
          if (r.success && r.object) {
            return {
              ...r,
              object: transformToSimpleDefinition(
                r.object as unknown as Record<string, unknown>,
              ),
            };
          }
          return r;
        });

        // Check for duplicate identifier_values across all rows
        const allIdentifierValues = new Map<string, number>();
        for (let i = 0; i < transformed.length; i++) {
          const def = transformed[i];
          const config = (def.object?.configuration as Record<string, unknown>)
            ?.generic_csv as { fields: FieldMapping[] } | undefined;
          if (!config?.fields) continue;
          for (const f of config.fields) {
            const existing = allIdentifierValues.get(f.identifier_value);
            if (existing !== undefined) {
              // Mark both rows as having errors
              const dupError = {
                field: "fields",
                error: `Duplicate identifier_value "${f.identifier_value}" (also in row ${existing + 1})`,
              };
              transformed[i] = {
                ...transformed[i],
                success: false,
                errors: [...(transformed[i].errors ?? []), dupError],
              };
              if (transformed[existing].success) {
                transformed[existing] = {
                  ...transformed[existing],
                  success: false,
                  errors: [
                    ...(transformed[existing].errors ?? []),
                    {
                      field: "fields",
                      error: `Duplicate identifier_value "${f.identifier_value}" (also in row ${i + 1})`,
                    },
                  ],
                };
              }
            } else {
              allIdentifierValues.set(f.identifier_value, i);
            }
          }
        }

        setDefinitions(transformed);
        setState("preview");
      })
      .finally(() => setParsing(false));
  };

  const handleUpload = (): void => {
    const valid = definitions
      .filter((d) => d.success && d.object)
      .map(
        (d) =>
          removeMapBlanks(
            d.object as SimpleAssetDefinition,
          ) as SimpleAssetDefinition,
      );
    onUpload(valid)
      .then(() => setState("complete"))
      .catch(() => {
        /* error displayed via ErrorBanner from the error prop */
      });
  };

  const handleCancel = (): void => {
    setDefinitions([]);
    setState("upload");
  };

  const validCount = definitions.filter((d) => d.success).length;

  return (
    <>
      {state === "upload" && (
        <BlockSpinner loading={parsing}>
          <div className="space-y-4">
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              Upload a CSV to create child asset positions with locations and
              field mapping configurations. The serial number is used as the
              asset identifier for CSV data matching.
            </p>
            <div className="text-sm text-zinc-600 dark:text-zinc-400 space-y-2">
              <p>
                <strong>Required columns:</strong>
              </p>
              <ul className="list-disc ml-6 space-y-1">
                <li>
                  <code>serial_number</code> — Asset serial number (also used as
                  the asset identifier)
                </li>
                <li>
                  <code>asset_type</code> — The type of meter asset
                </li>
                <li>
                  <code>address</code> — Location address
                </li>
                <li>
                  <code>fields.0.identifier_value</code>,{" "}
                  <code>fields.0.standardised_column</code>,{" "}
                  <code>fields.0.units</code> — Field mappings (use{" "}
                  <code>fields.1.*</code> for additional mappings)
                </li>
              </ul>
              <p>
                <strong>Optional columns:</strong>{" "}
                <code>asset_position_reference</code>,{" "}
                <code>location_reference</code>, <code>group</code>
              </p>
            </div>

            <div className="space-y-2 text-sm text-zinc-600 dark:text-zinc-400">
              <ReferenceSection
                title="valid asset types"
                items={VALID_ASSET_TYPES}
              />
              <ReferenceSection
                title="supported units"
                items={SUPPORTED_UNITS}
              />
              <ReferenceSection
                title="standardised column names"
                items={STANDARDISED_COLUMNS}
              />
            </div>

            <p className="text-xs text-zinc-500 dark:text-zinc-500">
              Units are normalised automatically — e.g. <code>degC</code>,{" "}
              <code>deg C</code>, and <code>celsius</code> are all converted to{" "}
              <code>°C</code>.
            </p>

            <BulkUpdateCSVDropZone
              buttonLabel="Upload Generic CSV Asset Register"
              parseComplete={handleFile}
            />
          </div>
        </BlockSpinner>
      )}

      {state === "preview" && (
        <BlockSpinner loading={loading}>
          <div className="space-y-4">
            <PreviewTable definitions={definitions} />

            <div className="flex gap-3">
              <Button
                color="brandLight"
                onClick={handleUpload}
                disabled={validCount === 0 || loading}
              >
                Upload {validCount} asset{validCount !== 1 ? "s" : ""}
              </Button>
              <Button color="red" onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
            </div>
          </div>
          <ErrorBanner error={error} />
        </BlockSpinner>
      )}

      {state === "complete" && (
        <div className="space-y-4">
          <AlertBanner className="bg-green-700">
            <Heading>Complete</Heading>
            <p>
              Successfully uploaded {validCount} asset position
              {validCount !== 1 ? "s" : ""}.
            </p>
          </AlertBanner>
          <Button outline onClick={handleCancel}>
            Upload More
          </Button>
        </div>
      )}
    </>
  );
}
